import React, { useState, useEffect, useCallback, useContext } from 'react';
import './Reviews.css';
import { FaStar } from 'react-icons/fa';
import { UrlContext } from '../../Context/UrlContext';

function Reviews() {

      const { apiBaseUrl } = useContext(UrlContext);

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(5);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000; // 2 seconds

  // Handle responsive visible count
  useEffect(() => {
    const updateVisibleCount = () => {
      const width = window.innerWidth;
      if (width >= 1400) setVisibleCount(5);
      else if (width >= 1200) setVisibleCount(4);
      else if (width >= 992) setVisibleCount(3);
      else if (width >= 768) setVisibleCount(2);
      else setVisibleCount(1);
    };

    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, []);

  // Auto rotate reviews every 10 seconds
  useEffect(() => {
    if (reviews.length > 0) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % reviews.length);
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [reviews]);

  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${apiBaseUrl}/reviews`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        mode: 'cors',
        cache: 'no-cache'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Network response was not ok' }));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!Array.isArray(data)) {
        throw new Error('Invalid response format: expected an array');
      }
      
      setReviews(data);
      setRetryCount(0); // Reset retry count on successful fetch
      setError(null);
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError(err.message || 'Failed to fetch reviews');
      
      // Implement exponential backoff for retries
      if (retryCount < MAX_RETRIES) {
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          fetchReviews();
        }, RETRY_DELAY * Math.pow(2, retryCount));
      }
    } finally {
      setLoading(false);
    }
  }, [retryCount]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const renderStars = useCallback((rating) => {
    return [...Array(5)].map((_, index) => (
      <FaStar
        key={index}
        className={index < rating ? 'star filled' : 'star'}
      />
    ));
  }, []);

  const handlePrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? reviews.length - 1 : prev - 1));
  }, [reviews.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % reviews.length);
  }, [reviews.length]);

  if (loading) {
    return (
      <div className="reviews-loading">
        <div className="loading-spinner"></div>
        <p>Loading reviews...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reviews-error">
        <p>Error loading reviews: {error}</p>
        {retryCount < MAX_RETRIES ? (
          <div className="error-help">
            <p>Attempting to reconnect... ({retryCount + 1}/{MAX_RETRIES})</p>
            <button onClick={fetchReviews} className="retry-button">
              Retry Now
            </button>
          </div>
        ) : (
          <div className="error-help">
            <p>Unable to load reviews after several attempts.</p>
            <p>Please check your internet connection and try again.</p>
            <button onClick={() => { setRetryCount(0); fetchReviews(); }} className="retry-button">
              Try Again
            </button>
          </div>
        )}
      </div>
    );
  }

  if (!reviews || reviews.length === 0) {
    return (
      <div className="reviews-empty">
        <p>No reviews available yet. Be the first to review!</p>
      </div>
    );
  }

  const getCurrentReviews = () => {
    const result = [];
    for (let i = 0; i < visibleCount; i++) {
      const index = (currentIndex + i) % reviews.length;
      result.push(reviews[index]);
    }
    return result;
  };

  const currentReviews = getCurrentReviews();

  return (
    <div className="reviews-container">
      <div className="reviews-grid">
        {currentReviews.map((review, index) => (
          <div key={`${review.id}-${index}`} className="review-card">
            <div className="review-header">
              <div className="product-info">
                {review.product_image && (
                  <div className="product-image">
                    <img
                      src={`data:image/jpeg;base64,${review.product_image}`}
                      alt={review.product_name}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/placeholder-image.jpg';
                      }}
                    />
                  </div>
                )}
                <div className="product-details">
                  <h3 className="product-name">{review.product_name}</h3>
                  <div className="review-rating">
                    {renderStars(review.rating)}
                    <span className="rating-text">{review.rating}/5</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="review-content">
              <p className="review-text">{review.review_text}</p>
              <div className="review-footer">
                <div className="review-author">
                  <p className="author-name">{review.user_name}</p>
                  <p className="review-date">
                    {new Date(review.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {reviews.length > visibleCount && (
        <div className="reviews-navigation">
          <button
            className="nav-button"
            onClick={handlePrevious}
            aria-label="Previous review"
          >
            Previous
          </button>
          <button
            className="nav-button"
            onClick={handleNext}
            aria-label="Next review"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default Reviews;
